<template>
  <div>
    <!-- Filter Card -->
    <b-card :title="$t('settlement.title')">
      <nav
        v-if="navigation"
        :aria-label="$t('downlineReports.trail')"
        class="mb-1"
      >
        <span
          v-for="(agent, index) in navigation.trail"
          :key="agent"
        >
          <span
            v-if="index"
            class="mx-50"
            aria-hidden="true"
          >›</span>
          <b-button
            v-if="agent !== navigation.agent"
            variant="link"
            class="p-0"
            :disabled="loading"
            @click="drillInto(agent)"
          >{{ agent }}</b-button>
          <span
            v-else
            aria-current="page"
          >{{ agent }}</span>
        </span>
      </nav>
      <b-form @submit.prevent>
        <b-row class="mt-1 text-left">
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group
              :label="$t('settlement.startDate')"
              label-for="start-date"
            >
              <flat-pickr
                v-model="startDate"
                class="form-control"
                :config="{ disableMobile:true, dateFormat: 'd/m/Y' }"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group
              :label="$t('settlement.endDate')"
              label-for="end-date"
            >
              <flat-pickr
                v-model="endDate"
                class="form-control"
                :config="{ disableMobile:true, dateFormat: 'd/m/Y' }"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group
              :label="$t('settlement.agent')"
              label-for="agent"
            >
              <agent-selector
                v-model="selectedAgent"
                :auto-select-default="false"
                :placeholder="$t('settlement.agentPlaceholder')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <div class="settlement-actions">
          <!-- ช่วงยอดนิยม — กดแล้วเติมวันที่ให้พร้อมค้นหาเลย ไม่ต้องเปิดปฏิทินสองรอบ -->
          <div class="settlement-quick-range">
            <b-button-group
              v-for="group in dateRangePresetGroups"
              :key="group.key"
              size="sm"
            >
              <b-button
                v-for="preset in group.presets"
                :key="preset.key"
                :variant="activePreset === preset.key ? 'primary' : 'outline-secondary'"
                :disabled="loading"
                @click.prevent="applyPreset(preset)"
              >
                {{ $t(preset.labelKey) }}
              </b-button>
            </b-button-group>
          </div>
          <div class="settlement-actions-buttons">
            <!-- สั่งคำนวณสรุปยอดใหม่ — เฉพาะ master ที่ถือสิทธิ์ manage all (ฝั่ง API เช็คซ้ำอีกชั้น) -->
            <b-button
              v-if="canRecalculate"
              v-ripple.400="'rgba(0, 0, 0, 0.15)'"
              variant="outline-primary"
              class="mr-1"
              :disabled="recalculating || recalcRunning"
              @click.prevent="onRecalculate"
            >
              <b-spinner
                v-if="recalculating || recalcRunning"
                small
                class="mr-50"
              />
              {{ $t('settlement.recalculate') }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="primary"
              @click.prevent="onSubmit"
            >
              {{ $t('settlement.search') }}
            </b-button>
          </div>
        </div>

        <!-- ความคืบหน้าจริงของงานคำนวณ — อ่านจาก server ทุก 2 วินาที จึงตรงกันทุก tab -->
        <div
          v-if="recalcJob"
          class="settlement-recalc-progress mt-1"
        >
          <b-alert
            v-if="recalcJob.status === 'failed'"
            show
            variant="danger"
            class="mb-0"
          >
            <div class="d-flex align-items-center justify-content-between">
              <span>{{ $t('settlement.recalcFailed', { error: recalcJob.error || '-' }) }}</span>
              <b-button
                size="sm"
                variant="outline-danger"
                @click.prevent="dismissRecalc"
              >
                {{ $t('settlement.recalcDismiss') }}
              </b-button>
            </div>
          </b-alert>

          <b-alert
            v-else-if="recalcJob.stale"
            show
            variant="warning"
            class="mb-0"
          >
            <div class="d-flex align-items-center justify-content-between">
              <span>{{ $t('settlement.recalcStale', { by: recalcJob.createdBy }) }}</span>
              <b-button
                size="sm"
                variant="outline-warning"
                @click.prevent="dismissRecalc"
              >
                {{ $t('settlement.recalcDismiss') }}
              </b-button>
            </div>
          </b-alert>

          <template v-else>
            <div class="d-flex align-items-center justify-content-between mb-25">
              <span class="text-primary">
                <b-spinner
                  small
                  class="mr-50"
                />
                {{ recalcPhaseLabel }}
                <small class="text-muted ml-50">
                  {{ $t('settlement.recalcStartedBy', { by: recalcJob.createdBy }) }}
                </small>
              </span>
              <small
                v-if="recalcJob.total"
                class="text-muted"
              >{{ recalcJob.done }} / {{ recalcJob.total }}</small>
            </div>
            <!--
              striped/animated ติดไว้ตลอด ไม่ใช่เฉพาะตอนยังไม่รู้จำนวนหน่วย —
              ตอนนั้นแถบกว้าง 0 พอดี ลายวิ่งจึงไม่มีที่ให้แสดงและมองไม่เห็นอะไรเลย
              ส่วนตัวบอกว่า "กำลังเริ่ม" มี spinner กับข้อความข้างบนทำหน้าที่อยู่แล้ว
            -->
            <b-progress
              :value="recalcPercent"
              max="100"
              variant="primary"
              height="6px"
              striped
              animated
            />
          </template>
        </div>
      </b-form>
    </b-card>

    <!-- Self Spread Card (spread ของ agent ผู้เรียกเอง — ที่เดียวที่เจ้าตัวเห็น P/L spread ตัวเอง) -->
    <b-card
      v-if="!loading && selfSpread && (selfSpread.pay !== 0 || selfSpread.cashBack !== 0)"
      class="settlement-self-spread"
    >
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <strong>{{ $t('settlement.selfSpread') }}</strong>
        <div
          class="d-flex flex-wrap"
          style="gap: 1.5rem"
        >
          <span>
            {{ $t('settlement.spreadPay') }}:
            <span
              :class="textColor(selfSpread.pay)"
              class="font-weight-bolder"
            >{{ toNumber(selfSpread.pay.toFixed(2)) }}</span>
          </span>
          <span>
            {{ $t('settlement.spreadCashBack') }}:
            <span
              :class="textColor(selfSpread.cashBack)"
              class="font-weight-bolder"
            >{{ toNumber(selfSpread.cashBack.toFixed(2)) }}</span>
          </span>
          <span>
            {{ $t('settlement.total') }}:
            <span
              :class="textColor(selfSpread.total)"
              class="font-weight-bolder"
            >{{ toNumber(selfSpread.total.toFixed(2)) }}</span>
          </span>
        </div>
      </div>
    </b-card>

    <!-- Data Table Card -->
    <b-card>
      <!-- ระหว่างโหลดใช้ตารางจริงเป็นโครง แล้วปิดตัวเลขด้วยแถบ shimmer —
           หัวตารางสองชั้นกับแถวรวมจึงสูงเท่าของจริงตั้งแต่เฟรมแรก ไม่ต้องเดาเป็นพิกเซล -->
      <div
        class="settlement-table-wrap"
        :class="{ 'settlement-table--loading': loading }"
      >
        <b-table
          ref="table"
          responsive
          :fields="columns"
          :items="tableItems"
          :sort-by.sync="pagination.sortBy"
          :sort-desc.sync="pagination.sortDesc"
          bordered
          show-empty
          :empty-text="$t('settlement.empty')"
          @sort-changed="onSortChanged"
        >
          <template #thead-top>
            <tr class="settlement-group-row">
              <th
                rowspan="2"
                class="settlement-rowspan-header settlement-sortable-header text-nowrap"
                style="min-width: 120px;"
                role="button"
                tabindex="0"
                @click="setSort('agent')"
                @keydown.enter.prevent="setSort('agent')"
                @keydown.space.prevent="setSort('agent')"
              >
                {{ $t('settlement.account') }} <span class="settlement-sort-indicator">{{ sortIndicator('agent') }}</span>
              </th>
              <th
                rowspan="2"
                class="settlement-rowspan-header settlement-sortable-header text-nowrap"
                style="min-width: 140px;"
                role="button"
                tabindex="0"
                @click="setSort('nickName')"
                @keydown.enter.prevent="setSort('nickName')"
                @keydown.space.prevent="setSort('nickName')"
              >
                {{ $t('settlement.name') }} <span class="settlement-sort-indicator">{{ sortIndicator('nickName') }}</span>
              </th>
              <th
                rowspan="2"
                class="settlement-rowspan-header settlement-sortable-header text-nowrap text-right"
                style="min-width: 90px;"
                role="button"
                tabindex="0"
                @click="setSort('bet')"
                @keydown.enter.prevent="setSort('bet')"
                @keydown.space.prevent="setSort('bet')"
              >
                {{ $t('settlement.bet') }} <span class="settlement-sort-indicator">{{ sortIndicator('bet') }}</span>
              </th>
              <th
                rowspan="2"
                class="settlement-rowspan-header settlement-sortable-header text-nowrap text-right"
                style="min-width: 90px;"
                role="button"
                tabindex="0"
                @click="setSort('netAmount')"
                @keydown.enter.prevent="setSort('netAmount')"
                @keydown.space.prevent="setSort('netAmount')"
              >
                {{ $t('settlement.net') }} <span class="settlement-sort-indicator">{{ sortIndicator('netAmount') }}</span>
              </th>
              <th
                colspan="4"
                class="settlement-group-header bg-light-info text-info"
              >
                {{ $t('settlement.member') }}
              </th>
              <th
                colspan="6"
                class="settlement-group-header bg-light-warning text-warning"
              >
                {{ $t('settlement.agentGroup') }}
              </th>
              <th
                colspan="4"
                class="settlement-group-header bg-light-success text-success"
              >
                {{ $t('settlement.company') }}
              </th>
            </tr>
          </template>

          <!-- Agent Column -->
          <template #cell(agent)="v">
            <button
              v-if="!loading && v.item.canOpenSummary === true"
              v-b-tooltip.hover.focus
              type="button"
              class="btn btn-link p-0 font-weight-bolder"
              :title="$t('downlineReports.viewSummary')"
              @click="drillInto(v.item.agent)"
            >{{ v.value }}</button>
            <span
              v-else
              class="font-weight-bolder"
            >{{ v.value }}</span>
          </template>

          <!-- Bet Column -->
          <template #cell(bet)="v">
            <button
              v-if="!loading && v.item.canReadDownlineBets === true"
              v-b-tooltip.hover.focus
              type="button"
              class="settlement-bet-link"
              :title="$t('poyReport.viewPoys')"
              :aria-label="`${$t('poyReport.viewPoys')} ${v.item.agent}: ${toNumber(v.value.toFixed(2))}`"
              @click="openPoys(v.item.agent)"
            >{{ toNumber(v.value.toFixed(2)) }}</button>
            <span
              v-else
              class="font-weight-bolder"
            >{{ toNumber(v.value.toFixed(2)) }}</span>
          </template>

          <!-- NetAmount Column -->
          <template #cell(netAmount)="v">
            <span
              :class="textColor(v.value)"
              class="font-weight-bolder"
            >
              {{ toNumber(v.value.toFixed(2)) }}
            </span>
          </template>

          <!-- Member Sent -->
          <template #cell(memberSent)="v">
            <span :class="textColor(v.item.member.sent)">
              {{ toNumber(v.item.member.sent.toFixed(2)) }}
            </span>
          </template>

          <!-- Member Discount -->
          <template #cell(memberDiscount)="v">
            <span :class="textColor(v.item.member.discount)">
              {{ toNumber(v.item.member.discount.toFixed(2)) }}
            </span>
          </template>

          <!-- Member Win -->
          <template #cell(memberWin)="v">
            <span :class="textColor(v.item.member.win)">
              {{ toNumber(v.item.member.win.toFixed(2)) }}
            </span>
          </template>

          <!-- Member Total -->
          <template #cell(memberTotal)="v">
            <span
              :class="textColor(v.item.member.total)"
              class="font-weight-bolder"
            >
              {{ toNumber(v.item.member.total.toFixed(2)) }}
            </span>
          </template>

          <!-- Agent Accepted -->
          <template #cell(agentAccepted)="v">
            <span :class="textColor(v.item.agentShare.accepted)">
              {{ toNumber(v.item.agentShare.accepted.toFixed(2)) }}
            </span>
          </template>

          <!-- Agent Discount -->
          <template #cell(agentDiscount)="v">
            <span :class="textColor(v.item.agentShare.discount)">
              {{ toNumber(v.item.agentShare.discount.toFixed(2)) }}
            </span>
          </template>

          <!-- Agent Win -->
          <template #cell(agentWin)="v">
            <span :class="textColor(v.item.agentShare.win)">
              {{ toNumber(v.item.agentShare.win.toFixed(2)) }}
            </span>
          </template>

          <!-- Agent Total -->
          <template #cell(agentTotal)="v">
            <span
              :class="textColor(v.item.agentShare.total)"
              class="font-weight-bolder"
            >
              {{ toNumber(v.item.agentShare.total.toFixed(2)) }}
            </span>
          </template>

          <!-- Agent Spread (ของผู้ตั้งเรทแถวนี้ — กำไร = บวก) -->
          <template #cell(agentSpread)="v">
            <span
              v-b-tooltip.hover
              :class="textColor(rowSpread(v.item).total)"
              :title="`${$t('settlement.spreadPay')}: ${toNumber(rowSpread(v.item).pay.toFixed(2))} | ${$t('settlement.spreadCashBack')}: ${toNumber(rowSpread(v.item).cashBack.toFixed(2))}`"
            >
              {{ toNumber(rowSpread(v.item).total.toFixed(2)) }}
            </span>
          </template>

          <!-- Agent Net Total (ฐาน + spread) -->
          <template #cell(agentNetTotal)="v">
            <span
              :class="textColor(rowNetTotal(v.item))"
              class="font-weight-bolder"
            >
              {{ toNumber(rowNetTotal(v.item).toFixed(2)) }}
            </span>
          </template>

          <!-- Company Accepted -->
          <template #cell(companyAccepted)="v">
            <span :class="textColor(v.item.company.accepted)">
              {{ toNumber(v.item.company.accepted.toFixed(2)) }}
            </span>
          </template>

          <!-- Company Discount -->
          <template #cell(companyDiscount)="v">
            <span :class="textColor(v.item.company.discount)">
              {{ toNumber(v.item.company.discount.toFixed(2)) }}
            </span>
          </template>

          <!-- Company Win -->
          <template #cell(companyWin)="v">
            <span :class="textColor(v.item.company.win)">
              {{ toNumber(v.item.company.win.toFixed(2)) }}
            </span>
          </template>

          <!-- Company Total -->
          <template #cell(companyTotal)="v">
            <span
              :class="textColor(v.item.company.total)"
              class="font-weight-bolder"
            >
              {{ toNumber(v.item.company.total.toFixed(2)) }}
            </span>
          </template>

          <!-- Footer (Totals Row) -->
          <template #custom-foot>
            <tr
              v-if="displayTotals"
              class="table-active font-weight-bolder"
            >
              <td
                colspan="2"
                class="text-right"
              >
                {{ $t('settlement.total') }}
              </td>
              <td class="text-right">
                {{ toNumber(displayTotals.bet.toFixed(2)) }}
              </td>
              <td class="text-right">
                <span :class="textColor(displayTotals.netAmount)">
                  {{ toNumber(displayTotals.netAmount.toFixed(2)) }}
                </span>
              </td>
              <!-- Member -->
              <td class="text-right">
                <span :class="textColor(displayTotals.member.sent)">
                  {{ toNumber(displayTotals.member.sent.toFixed(2)) }}
                </span>
              </td>
              <td class="text-right">
                <span :class="textColor(displayTotals.member.discount)">
                  {{ toNumber(displayTotals.member.discount.toFixed(2)) }}
                </span>
              </td>
              <td class="text-right">
                <span :class="textColor(displayTotals.member.win)">
                  {{ toNumber(displayTotals.member.win.toFixed(2)) }}
                </span>
              </td>
              <td class="text-right">
                <span :class="textColor(displayTotals.member.total)">
                  {{ toNumber(displayTotals.member.total.toFixed(2)) }}
                </span>
              </td>
              <!-- Agent -->
              <td class="text-right">
                <span :class="textColor(displayTotals.agentShare.accepted)">
                  {{ toNumber(displayTotals.agentShare.accepted.toFixed(2)) }}
                </span>
              </td>
              <td class="text-right">
                <span :class="textColor(displayTotals.agentShare.discount)">
                  {{ toNumber(displayTotals.agentShare.discount.toFixed(2)) }}
                </span>
              </td>
              <td class="text-right">
                <span :class="textColor(displayTotals.agentShare.win)">
                  {{ toNumber(displayTotals.agentShare.win.toFixed(2)) }}
                </span>
              </td>
              <td class="text-right">
                <span :class="textColor(displayTotals.agentShare.total)">
                  {{ toNumber(displayTotals.agentShare.total.toFixed(2)) }}
                </span>
              </td>
              <td class="text-right">
                <span :class="textColor(displayTotals.spread ? displayTotals.spread.total : 0)">
                  {{ toNumber((displayTotals.spread ? displayTotals.spread.total : 0).toFixed(2)) }}
                </span>
              </td>
              <td class="text-right">
                <span :class="textColor(displayTotals.netTotal || 0)">
                  {{ toNumber((displayTotals.netTotal || 0).toFixed(2)) }}
                </span>
              </td>
              <!-- Company -->
              <td class="text-right">
                <span :class="textColor(displayTotals.company.accepted)">
                  {{ toNumber(displayTotals.company.accepted.toFixed(2)) }}
                </span>
              </td>
              <td class="text-right">
                <span :class="textColor(displayTotals.company.discount)">
                  {{ toNumber(displayTotals.company.discount.toFixed(2)) }}
                </span>
              </td>
              <td class="text-right">
                <span :class="textColor(displayTotals.company.win)">
                  {{ toNumber(displayTotals.company.win.toFixed(2)) }}
                </span>
              </td>
              <td class="text-right">
                <span :class="textColor(displayTotals.company.total)">
                  {{ toNumber(displayTotals.company.total.toFixed(2)) }}
                </span>
              </td>
            </tr>
          </template>
        </b-table>

        <!-- ทับทั้งตารางไว้ กันคลิกจัดเรียงระหว่างที่ข้อมูลยังเป็นของปลอม -->
        <div
          v-if="loading"
          class="skeleton-overlay"
        >
          <b-spinner variant="primary" />
        </div>
      </div>

      <!-- Pagination -->
      <!-- จอกว้างวางซ้าย-กลาง-ขวา แต่พอจอเล็กทั้งสามกองซ้อนกันเป็นสามบรรทัด
           ถ้ายังจัดคนละทิศจะอ่านเป็นของคนละชุด — จอเล็กจึงจัดกึ่งกลางให้หมด -->
      <b-row
        v-if="!loading && totalCount > 0"
        class="mt-2 settlement-pagination-row"
      >
        <b-col
          cols="12"
          md="3"
          class="d-flex align-items-center justify-content-center justify-content-md-start"
        >
          <label class="mb-0 mr-50 font-small-4 text-nowrap">{{ $t('settlement.perPage') }}</label>
          <b-form-select
            v-model="pagination.rowsPerPage"
            size="sm"
            :options="rowsOptions"
            class="w-auto"
            style="min-width: 80px"
            @change="onSubmit"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="d-flex justify-content-center"
        >
          <b-pagination
            v-model="pagination.page"
            :total-rows="totalCount"
            :per-page="pagination.rowsPerPage"
            align="center"
            size="sm"
            class="my-0"
            @change="onPageChange"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="text-center text-md-right"
        >
          <span class="text-muted font-small-4">
            {{ $t('settlement.showing') }} {{ (pagination.page - 1) * pagination.rowsPerPage + 1 }} -
            {{ Math.min(pagination.page * pagination.rowsPerPage, totalCount) }}
            {{ $t('settlement.from') }} {{ totalCount }} {{ $t('settlement.records') }}
          </span>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BCol,
  BForm,
  BFormGroup,
  BFormSelect,
  BPagination,
  BProgress,
  BRow,
  BSpinner,
  BTable,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import flatPickr from 'vue-flatpickr-component'
import AgentSelector from '@core/components/agent/AgentSelector.vue'
import { dateRangePresetGroups, dateRangePresets } from '@/utils/dateRangePresets'
import settlementService from '@/service/settlementService'
import lottoService from '@/service/lottoService'
// สั่งคำนวณสรุปยอดใหม่อยู่ที่ huay-service (มี Kafka producer) ไม่ใช่ report-service

// แถวหลอกระหว่างโหลด — ต้องมีคีย์ครบเหมือนแถวจริง เพราะ cell slot เรียก .toFixed() ตรง ๆ
const emptyShare = () => ({
  sent: 0, accepted: 0, discount: 0, win: 0, total: 0,
})

/** จังหวะถามสถานะงานคำนวณ — ถี่พอให้แถบขยับลื่น แต่ไม่ถี่จนยิง API เปล่าทั้งวัน */
const RECALC_POLL_MS = 2000

/** งานที่เงียบไปแล้วยังต้องถามต่อ เผื่อ worker ฟื้น — แค่ไม่ต้องถี่เท่างานที่กำลังเดิน */
const RECALC_POLL_STALE_MS = 10000

/**
 * จำนวนครั้งที่ยอมให้ถามสถานะไม่สำเร็จติดกันก่อนเลิกตาม
 *
 * ต้องมีเพดาน ไม่ใช่ตามตลอดกาล (service ตายจริงก็จะยิงเปล่าไม่จบ) และต้องไม่ใช่ 1
 * เพราะเน็ตสะดุดรอบเดียวไม่ควรทำให้ปุ่มค้าง disabled จนกว่าจะ refresh หน้า
 */
const RECALC_POLL_MAX_ERRORS = 5

/** จำนวนรอบที่ยอมตามงานที่เงียบไปแล้ว (× RECALC_POLL_STALE_MS ≈ 5 นาที) ก่อนเลิกถาม */
const RECALC_STALE_MAX_ROUNDS = 30

const skeletonRecord = () => ({
  agent: '—',
  nickName: '—',
  bet: 0,
  netAmount: 0,
  member: emptyShare(),
  agentShare: emptyShare(),
  company: emptyShare(),
  spread: { pay: 0, cashBack: 0, total: 0 },
  netTotal: 0,
})

export default {
  components: {
    flatPickr,
    BTable,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BForm,
    BButton,
    BButtonGroup,
    BSpinner,
    BPagination,
    BAlert,
    BProgress,
    AgentSelector,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      dateRangePresetGroups,
      dateRangePresets,
      loading: false,
      recalculating: false,
      /** สถานะงานคำนวณที่ยังไม่จบ — มาจาก server ไม่ใช่ความจำของ tab นี้ */
      recalcJob: null,
      recalcTimer: null,
      /** เลขรุ่นของลูป poll — คำขอที่ค้างกลางอากาศใช้เช็คว่าตัวเองตกรุ่นแล้วหรือยัง */
      recalcPollToken: 0,
      recalcPollErrors: 0,
      recalcStaleRounds: 0,
      startDate: this.formatDate(new Date(new Date().setDate(new Date().getDate() - 30))),
      endDate: this.formatDate(new Date()),
      selectedAgent: null,
      navigation: null,
      appliedScope: null,
      reportRequestId: 0,
      records: [],
      totals: null,
      selfSpread: null,
      totalCount: 0,
      pagination: {
        page: 1,
        rowsPerPage: 50,
        sortBy: 'agent',
        sortDesc: false,
      },
      rowsOptions: [10, 25, 50, 100, 250],
    }
  },
  computed: {
    toNumber() {
      return this.$store.state.app.toNumber
    },
    /**
     * ระหว่างโหลดป้อนแถวหลอกให้ตารางเดิม แทนที่จะสลับไปวาดกล่อง skeleton แยก
     * จำนวนแถวยึดของรอบก่อนไว้ก่อน ตารางจะได้ไม่กระโดดสูง-เตี้ยตอนข้อมูลมาถึง
     */
    tableItems() {
      if (!this.loading) return this.records
      const rows = this.records.length || 5
      return Array.from({ length: rows }, skeletonRecord)
    },
    /** แถวรวมต้องโผล่ตอนโหลดครั้งแรกด้วย ไม่งั้น skeleton เตี้ยกว่าตารางจริงอยู่หนึ่งแถว */
    displayTotals() {
      if (!this.loading) return this.totals
      return this.totals || skeletonRecord()
    },
    /**
     * ปุ่มช่วงที่ตรงกับวันที่ในฟอร์มตอนนี้ (null = ผู้ใช้เลือกช่วงเอง)
     * เทียบจากค่าจริงในช่องวันที่ ไม่เก็บ state แยก — พอผู้ใช้ไปแก้ปฏิทินเอง
     * ปุ่มจะเลิกไฮไลต์ให้เองโดยไม่ต้องมีใครคอยล้างค่า
     */
    activePreset() {
      const now = new Date()
      const matched = this.dateRangePresets.find(preset => {
        const [start, end] = preset.resolve(now)
        return this.formatDate(start) === this.startDate && this.formatDate(end) === this.endDate
      })
      return matched ? matched.key : null
    },
    /**
     * ตรงกับกติกาฝั่ง API (SummaryRecalcController.IsMasterManageAllAsync)
     * — ability manage/all อย่างเดียวไม่พอ ต้องเป็นบัญชีใต้ master ด้วย
     * ไม่งั้น member เก่าที่ยังถือ manage/all อยู่ใต้ agent อื่นจะเห็นปุ่มแล้วกดได้ 403
     * (แบบเดียวกับ views/account-members/SubAccounts.vue)
     */
    canRecalculate() {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      const isBackOfficeRole = userData.role === 'admin' || userData.role === 'sub'
      return isBackOfficeRole && userData.agent === 'master' && this.$can('manage', 'all')
    },
    /** งานกำลังเดินอยู่จริง — งานที่เงียบเกินกำหนดไม่นับ ไม่งั้นปุ่มถูกล็อกตลอดกาล */
    recalcRunning() {
      return !!this.recalcJob && this.recalcJob.status === 'running' && !this.recalcJob.stale
    },
    /**
     * ความคืบหน้ารวมคิดมาจาก server แล้ว — ห้ามหาร done/total เองที่นี่
     * สองค่านั้นเป็นของ "เฟสที่กำลังทำ" เฟสเดียว ใช้แทนความคืบหน้าทั้งงานไม่ได้
     */
    recalcPercent() {
      if (!this.recalcJob) return 0
      return Math.min(100, Math.max(0, this.recalcJob.percent || 0))
    },
    /**
     * ข้อความบอกว่ากำลังทำเฟสไหน
     *
     * ตอนที่ยังไม่มี worker ตัวไหนรับงาน (ไม่รู้จำนวนหน่วย และยังไม่มีความคืบหน้าใด ๆ)
     * บอกว่า "กำลังเริ่ม" แทนที่จะโชว์ชื่อเฟสค้างไว้ให้เข้าใจผิดว่ากำลังทำอยู่แล้ว
     * แต่ถ้าเดินไปได้บ้างแล้ว ให้ขึ้นชื่อเฟสเสมอ — ช่วงรอยต่อระหว่างเฟสก็ไม่ถอยกลับไป "กำลังเริ่ม"
     */
    recalcPhaseLabel() {
      if (!this.recalcJob) return ''
      if (!this.recalcJob.total && !this.recalcPercent) return this.$t('settlement.recalcPhaseStarting')
      const key = {
        Result: 'settlement.recalcPhaseResult',
        Summary: 'settlement.recalcPhaseSummary',
        Invoice: 'settlement.recalcPhaseInvoice',
      }[this.recalcJob.phase]
      return key ? this.$t(key) : this.$t('settlement.recalcPhaseStarting')
    },
    columns() {
      const t = key => this.$t(key)

      return [
        {
          key: 'agent',
          label: t('settlement.account'),
          class: 'text-nowrap',
          thClass: 'settlement-hidden-field-header',
          thStyle: { minWidth: '120px' },
          sortable: true,
        },
        {
          key: 'nickName',
          label: t('settlement.name'),
          class: 'text-nowrap',
          thClass: 'settlement-hidden-field-header',
          thStyle: { minWidth: '140px' },
          sortable: true,
        },
        {
          key: 'bet',
          label: t('settlement.bet'),
          class: 'text-nowrap text-right',
          thClass: 'settlement-hidden-field-header',
          thStyle: { minWidth: '90px' },
          sortable: true,
        },
        {
          key: 'netAmount',
          label: t('settlement.net'),
          class: 'text-nowrap text-right',
          thClass: 'settlement-hidden-field-header',
          thStyle: { minWidth: '90px' },
          sortable: true,
        },
        {
          key: 'memberSent',
          label: t('settlement.sent'),
          class: 'text-nowrap text-right',
          thClass: 'bg-light-info',
          thStyle: { minWidth: '90px' },
        },
        {
          key: 'memberDiscount',
          label: t('settlement.discount'),
          class: 'text-nowrap text-right',
          thClass: 'bg-light-info',
          thStyle: { minWidth: '80px' },
        },
        {
          key: 'memberWin',
          label: t('settlement.win'),
          class: 'text-nowrap text-right',
          thClass: 'bg-light-info',
          thStyle: { minWidth: '90px' },
        },
        {
          key: 'memberTotal',
          label: t('settlement.total'),
          class: 'text-nowrap text-right',
          thClass: 'bg-light-info',
          thStyle: { minWidth: '90px' },
        },
        {
          key: 'agentAccepted',
          label: t('settlement.accepted'),
          class: 'text-nowrap text-right',
          thClass: 'bg-light-warning',
          thStyle: { minWidth: '90px' },
        },
        {
          key: 'agentDiscount',
          label: t('settlement.discount'),
          class: 'text-nowrap text-right',
          thClass: 'bg-light-warning',
          thStyle: { minWidth: '80px' },
        },
        {
          key: 'agentWin',
          label: t('settlement.win'),
          class: 'text-nowrap text-right',
          thClass: 'bg-light-warning',
          thStyle: { minWidth: '90px' },
        },
        {
          key: 'agentTotal',
          label: t('settlement.total'),
          class: 'text-nowrap text-right',
          thClass: 'bg-light-warning',
          thStyle: { minWidth: '90px' },
          sortable: true,
        },
        {
          key: 'agentSpread',
          label: t('settlement.spread'),
          class: 'text-nowrap text-right',
          thClass: 'bg-light-warning',
          thStyle: { minWidth: '90px' },
        },
        {
          key: 'agentNetTotal',
          label: t('settlement.netWithSpread'),
          class: 'text-nowrap text-right',
          thClass: 'bg-light-warning',
          thStyle: { minWidth: '90px' },
        },
        {
          key: 'companyAccepted',
          label: t('settlement.accepted'),
          class: 'text-nowrap text-right',
          thClass: 'bg-light-success',
          thStyle: { minWidth: '90px' },
        },
        {
          key: 'companyDiscount',
          label: t('settlement.discount'),
          class: 'text-nowrap text-right',
          thClass: 'bg-light-success',
          thStyle: { minWidth: '80px' },
        },
        {
          key: 'companyWin',
          label: t('settlement.win'),
          class: 'text-nowrap text-right',
          thClass: 'bg-light-success',
          thStyle: { minWidth: '90px' },
        },
        {
          key: 'companyTotal',
          label: t('settlement.total'),
          class: 'text-nowrap text-right',
          thClass: 'bg-light-success',
          thStyle: { minWidth: '90px' },
          sortable: true,
        },
      ]
    },
  },
  watch: {
    '$route.query': {
      handler() {
        this.restoreReportRoute()
        this.loadReport()
      },
    },
  },
  beforeMount() {
    this.restoreReportRoute()
    this.loadReport()
    // ถามสถานะจาก server ทุกครั้งที่เปิดหน้า — นี่คือจุดที่ทำให้ refresh หรือเปิด tab ใหม่
    // แล้วยังเห็นงานเดิมที่ค้างอยู่ แทนที่จะขึ้นเหมือนไม่เคยกดอะไร
    if (this.canRecalculate) this.pollRecalc()
  },
  beforeDestroy() {
    this.reportRequestId += 1
    this.stopRecalcPolling()
  },
  methods: {
    restoreReportRoute() {
      const { agent, startDate, endDate } = this.$route.query
      this.selectedAgent = typeof agent === 'string' && agent
        ? { code: agent, username: agent, label: agent } : null
      if (typeof startDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
        this.startDate = startDate.split('-').reverse().join('/')
      }
      if (typeof endDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
        this.endDate = endDate.split('-').reverse().join('/')
      }
      const page = Number(this.$route.query.page)
      this.pagination.page = Number.isSafeInteger(page) && page > 0 ? page : 1
      const limit = Number(this.$route.query.limit)
      if (this.rowsOptions.includes(limit)) this.pagination.rowsPerPage = limit
      if (this.columns.some(field => field.key === this.$route.query.sortBy && field.sortable)) {
        this.pagination.sortBy = this.$route.query.sortBy
        this.pagination.sortDesc = this.$route.query.sortOrder === 'desc'
      }
    },
    navigateReport(query) {
      const destination = { name: 'settlement', query }
      if (this.$router.resolve(destination).route.fullPath === this.$route.fullPath) {
        this.loadReport()
      } else {
        this.$router.push(destination)
      }
    },
    drillInto(agent) {
      if (this.loading || !this.appliedScope) return
      this.navigateReport({ ...this.appliedScope, agent })
    },
    openPoys(agent) {
      if (this.loading || !this.appliedScope) return
      const summary = new URLSearchParams({
        ...this.appliedScope,
        agent: this.navigation.agent,
        page: this.pagination.page,
        limit: this.pagination.rowsPerPage,
        sortBy: this.pagination.sortBy,
        sortOrder: this.pagination.sortDesc ? 'desc' : 'asc',
      }).toString()
      this.$router.push({
        name: 'poy-report',
        query: { ...this.appliedScope, agent, summary },
      })
    },
    formatDate(date) {
      const d = date.getDate().toString().padStart(2, '0')
      const m = (date.getMonth() + 1).toString().padStart(2, '0')
      const y = date.getFullYear()
      return `${d}/${m}/${y}`
    },
    parseDate(dateStr) {
      const parts = dateStr.split('/')
      return new Date(parts[2], parts[1] - 1, parts[0])
    },
    /** yyyy-MM-dd ตามปฏิทินท้องถิ่น — ห้ามใช้ toISOString() ที่แปลงเป็น UTC ก่อน */
    toApiDate(date) {
      const m = (date.getMonth() + 1).toString().padStart(2, '0')
      const d = date.getDate().toString().padStart(2, '0')
      return `${date.getFullYear()}-${m}-${d}`
    },
    textColor(v) {
      if (v < 0) return 'text-danger'
      if (v > 0) return 'text-success'
      return ''
    },
    rowSpread(item) {
      return item.spread || { pay: 0, cashBack: 0, total: 0 }
    },
    rowNetTotal(item) {
      return item.netTotal != null ? item.netTotal : item.agentShare.total
    },
    sortIndicator(field) {
      if (this.pagination.sortBy !== field) return ''
      return this.pagination.sortDesc ? '↓' : '↑'
    },
    setSort(field) {
      if (this.pagination.sortBy === field) {
        this.pagination.sortDesc = !this.pagination.sortDesc
      } else {
        this.pagination.sortBy = field
        this.pagination.sortDesc = false
      }

      this.pagination.page = 1
      this.loadReport()
    },
    onSubmit() {
      this.pagination.page = 1
      const query = {
        startDate: this.toApiDate(this.parseDate(this.startDate)),
        endDate: this.toApiDate(this.parseDate(this.endDate)),
      }
      if (this.selectedAgent) query.agent = this.selectedAgent.code
      this.navigateReport(query)
    },
    applyPreset(preset) {
      const [start, end] = preset.resolve(new Date())
      this.startDate = this.formatDate(start)
      this.endDate = this.formatDate(end)
      this.onSubmit()
    },
    /**
     * อ่านสถานะงานคำนวณครั้งเดียว แล้วตั้งรอบถัดไปถ้ายังไม่จบ
     *
     * ใช้ setTimeout ต่อกันเป็นทอด ๆ ไม่ใช่ setInterval — ถ้า API ช้ากว่าช่วงเวลาที่ตั้งไว้
     * setInterval จะยิงซ้อนกันเรื่อย ๆ จนคิวบวม ส่วนแบบนี้จะรอรอบก่อนจบเสมอ
     *
     * `jobId` ว่าง = ถามว่า "ตอนนี้ระบบมีงานค้างอะไรอยู่" ซึ่งเป็นเส้นทางตอนเปิดหน้า
     */
    async pollRecalc(jobId) {
      this.stopRecalcPolling()
      const token = this.recalcPollToken

      try {
        const url = jobId ? `summary/recalculate/${jobId}` : 'summary/recalculate/active'
        const res = await lottoService.getAuthen(url)
        if (token !== this.recalcPollToken) return

        // 204 = ไม่มีงานค้าง ถือเป็นคำตอบปกติ ไม่ใช่ error
        this.recalcJob = res.status === 200 && res.data ? res.data : null
        this.recalcPollErrors = 0
      } catch (err) {
        if (token !== this.recalcPollToken) return

        // เงียบไว้ — poll พลาดหนึ่งรอบไม่ใช่เรื่องที่ต้องเด้ง toast ใส่หน้าคนใช้งาน
        // แต่ห้ามหยุดลูปทิ้ง: งานฝั่ง server ยังเดินอยู่ ถ้าเลิกตามตั้งแต่พลาดครั้งแรก
        // ปุ่มจะค้าง disabled และแถบค้างนิ่งจนกว่าจะ refresh หน้า ซึ่งแย่กว่าไม่มีแถบเลย
        this.recalcPollErrors += 1
        // eslint-disable-next-line no-console
        console.error('Failed to read recalculation status:', err)

        if (this.recalcPollErrors < RECALC_POLL_MAX_ERRORS) {
          // ถอยห่างขึ้นเรื่อย ๆ — ถ้า service กำลัง restart การรัวถามไม่ได้ช่วยให้มันกลับมาเร็วขึ้น
          this.recalcTimer = setTimeout(
            () => this.pollRecalc(jobId), RECALC_POLL_MS * this.recalcPollErrors,
          )
          return
        }

        // ยอมแพ้แล้วต้องคืนปุ่มให้ด้วย ไม่งั้นคนกดติดอยู่กับหน้าที่กดอะไรไม่ได้เลย
        // (สั่งซ้ำได้ปลอดภัย — ฝั่ง server มีด่านกันงานซ้อนของตัวเองอยู่แล้ว)
        this.recalcJob = null
        this.recalcPollErrors = 0
        return
      }

      if (!this.recalcJob) return

      if (this.recalcJob.status === 'done') {
        this.$bvToast.toast(this.$t('settlement.recalcFinished'), {
          title: this.$t('settlement.recalculateTitle'),
          variant: 'success',
          solid: true,
        })
        this.recalcJob = null
        return
      }

      if (this.recalcJob.status === 'failed') return

      // งานที่เงียบเกินกำหนดยังตามต่อ แค่ห่างขึ้น — worker อาจแค่ติดหนึบแล้วฟื้นกลับมา
      // ถ้าเลิกตามทันทีที่เห็นว่าค้าง แถบจะแช่อยู่ที่ "งานค้าง" ตลอดไปทั้งที่ของจริงเดินต่อจนจบแล้ว
      if (!this.recalcJob.stale) {
        this.recalcStaleRounds = 0
      } else {
        this.recalcStaleRounds += 1
        // แต่ไม่ตามตลอดกาล — tab ที่ถูกเปิดค้างทิ้งไว้ไม่ควรยิง API ไปจนกว่าจะมีคนมาปิด
        // แถบเตือนยังอยู่บนหน้าจอพร้อมปุ่มปิด แค่เลิกถามซ้ำเท่านั้น
        if (this.recalcStaleRounds > RECALC_STALE_MAX_ROUNDS) return
      }

      const nextDelay = this.recalcJob.stale ? RECALC_POLL_STALE_MS : RECALC_POLL_MS

      // เก็บ id ไว้ใน closure ไม่อ่านจาก this.recalcJob ตอน timer ทำงาน
      // เพราะระหว่างรอ ผู้ใช้อาจกดปิดจนมันกลายเป็น null ไปแล้ว
      const { jobId: nextId } = this.recalcJob
      this.recalcTimer = setTimeout(() => this.pollRecalc(nextId), nextDelay)
    },
    /**
     * หยุดลูป — เดินเลข token ด้วยทุกครั้ง
     *
     * เคลียร์ timer อย่างเดียวไม่พอ: คำขอที่ยิงไปแล้วและยังไม่กลับมาจะไม่รู้ว่ามีใครสั่งหยุด
     * แล้วตั้งรอบถัดไปต่อ — บน component ที่ถูกทำลายไปแล้วในกรณี beforeDestroy
     */
    stopRecalcPolling() {
      this.recalcPollToken += 1
      if (!this.recalcTimer) return
      clearTimeout(this.recalcTimer)
      this.recalcTimer = null
    },
    /** ปิดแถบแจ้งงานที่ล้มเหลว/ค้าง — ไม่ได้แตะข้อมูลฝั่ง server แค่เลิกแสดงใน tab นี้ */
    dismissRecalc() {
      this.stopRecalcPolling()
      this.recalcJob = null
      // เริ่มนับใหม่ ไม่งั้นความผิดพลาดของงานที่เพิ่งปิดไปจะถูกยกยอดไปกินโควตาของงานหน้า
      this.recalcPollErrors = 0
      this.recalcStaleRounds = 0
    },
    /**
     * สั่งคำนวณสรุปยอดใหม่ตามช่วงวันที่อยู่ใน filter
     *
     * งานวิ่งผ่าน Kafka จึงเป็น async — HTTP ตอบได้แค่ "ส่งเข้าคิวแล้ว" ไม่ใช่ "คำนวณเสร็จ"
     * ความคืบหน้าจริงจึงมาจากการ poll `jobId` ที่ได้กลับมา
     * ข้อความยืนยันบอกจำนวนวันและขอบเขต agent ให้ชัดก่อน เพราะกดแล้วหยุดกลางคันไม่ได้
     */
    onRecalculate() {
      const start = this.parseDate(this.startDate)
      const end = this.parseDate(this.endDate)

      if (start > end) {
        this.$bvToast.toast(this.$t('settlement.recalculateBadRange'), {
          title: this.$t('settlement.errorTitle'),
          variant: 'danger',
          solid: true,
        })
        return
      }

      const days = Math.round((end - start) / 86400000) + 1
      const scope = this.selectedAgent
        ? this.$t('settlement.recalculateScopeAgent', { agent: this.selectedAgent.code })
        : this.$t('settlement.recalculateScopeAll')

      this.$bvModal.msgBoxConfirm(
        this.$t('settlement.recalculateConfirm', {
          start: this.startDate,
          end: this.endDate,
          days,
          scope,
        }),
        {
          title: this.$t('settlement.recalculateTitle'),
          okVariant: 'primary',
          okTitle: this.$t('settlement.recalculateOk'),
          cancelTitle: this.$t('settlement.recalculateCancel'),
          cancelVariant: 'outline-secondary',
          centered: true,
        },
      ).then(async confirmed => {
        if (!confirmed) return

        this.recalculating = true
        try {
          const res = await lottoService.postAuthen('summary/recalculate', {
            // ส่งเป็น yyyy-MM-dd ไม่ใช่ toISOString() — ISO ลงท้าย Z จะกลายเป็นเวลา UTC
            // ทำให้ฝั่ง server ได้วันย้อนหลัง 1 วัน แล้วเขียนสรุปยอดลงคนละแถวกับที่ระบบอ่าน
            startDate: this.toApiDate(start),
            endDate: this.toApiDate(end),
            agent: this.selectedAgent ? this.selectedAgent.code : null,
          })

          if (res.status === 200) {
            this.$bvToast.toast(res.data.message || this.$t('settlement.recalculateQueued'), {
              title: this.$t('settlement.recalculateTitle'),
              variant: 'success',
              solid: true,
            })
            // เริ่มติดตามด้วย jobId ที่เพิ่งได้ ไม่ต้องรอ /active รอบถัดไป
            await this.pollRecalc(res.data.jobId)
          } else if (res.status === 409) {
            // มีคนอื่น (หรือ tab อื่น) สั่งไว้อยู่ — เกาะงานนั้นแทนการเด้ง error เฉย ๆ
            this.$bvToast.toast(res.data.message || this.$t('settlement.recalculateError'), {
              title: this.$t('settlement.recalculateTitle'),
              variant: 'warning',
              solid: true,
            })
            await this.pollRecalc(res.data.jobId)
          } else {
            this.$bvToast.toast(
              (res.data && res.data.message) || this.$t('settlement.recalculateError'),
              {
                title: this.$t('settlement.errorTitle'),
                variant: 'danger',
                solid: true,
              },
            )
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to queue summary recalculation:', err)
          this.$bvToast.toast(this.$t('settlement.recalculateError'), {
            title: this.$t('settlement.errorTitle'),
            variant: 'danger',
            solid: true,
          })
        } finally {
          this.recalculating = false
        }
      })
    },
    onPageChange(page) {
      this.pagination.page = page
      this.loadReport()
    },
    onSortChanged(ctx) {
      this.pagination.sortBy = ctx.sortBy
      this.pagination.sortDesc = ctx.sortDesc
      this.pagination.page = 1
      this.loadReport()
    },
    async loadReport() {
      this.reportRequestId += 1
      const requestId = this.reportRequestId
      this.loading = true

      const skip = (this.pagination.page - 1) * this.pagination.rowsPerPage
      const params = {
        startDate: this.toApiDate(this.parseDate(this.startDate)),
        endDate: this.toApiDate(this.parseDate(this.endDate)),
        skip,
        limit: this.pagination.rowsPerPage,
        sortBy: this.pagination.sortBy,
        sortOrder: this.pagination.sortDesc ? 'desc' : 'asc',
      }

      if (this.selectedAgent) {
        params.agent = this.selectedAgent.code
      }

      try {
        const res = await settlementService.getReport(params)
        if (requestId !== this.reportRequestId) return
        if (res.status !== 200) throw new Error('Report access failed')
        if (res.status === 200) {
          this.navigation = res.data.navigation || null
          this.appliedScope = { startDate: params.startDate, endDate: params.endDate }
          this.records = res.data.records || []
          this.totals = res.data.totals || null
          this.selfSpread = res.data.selfSpread || null
          this.totalCount = res.data.totalCount || 0
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load settlement report:', err)
        if (requestId !== this.reportRequestId) return
        this.records = []
        this.totals = null
        this.selfSpread = null
        this.totalCount = 0
        this.navigation = null
        this.appliedScope = null
        this.$bvToast.toast(this.$t('downlineReports.loadError'), {
          title: this.$t('settlement.errorTitle'),
          variant: 'danger',
          solid: true,
        })
      } finally {
        if (requestId === this.reportRequestId) this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.settlement-bet-link {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: var(--hdg-brand-primary, #c8102e);
  }

  &:focus-visible {
    border-radius: 2px;
    outline: 2px solid currentColor;
    outline-offset: 3px;
  }
}

.settlement-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.settlement-quick-range {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.settlement-actions-buttons {
  margin-left: auto;
}

// จอแคบ: ให้แต่ละกลุ่มกินเต็มบรรทัดและปุ่มในกลุ่มกว้างเท่ากันเป๊ะ
// ปล่อยให้ btn-group ห่อบรรทัดเองจะได้ปุ่มที่ตกแถวใหม่ยืดเต็มความกว้างแถวเดียวโดด ๆ
@media (max-width: 767.98px) {
  .settlement-quick-range,
  .settlement-actions-buttons {
    width: 100%;
    margin-left: 0;
  }

  .settlement-quick-range .btn-group {
    width: 100%;
  }

  // flex-basis 0 = แบ่งพื้นที่เท่ากันโดยไม่สนความยาวข้อความในปุ่ม
  .settlement-quick-range .btn,
  .settlement-actions-buttons .btn {
    flex: 1 1 0;
  }

  .settlement-actions-buttons {
    display: flex;
    gap: 0.5rem;

    .btn {
      margin-right: 0 !important;
    }
  }
}

.settlement-group-row {
  .settlement-rowspan-header {
    vertical-align: middle;
    background-color: #f9f7f7;
    color: #5d555a;
    font-weight: 700;
  }

  .settlement-sortable-header {
    cursor: pointer;
    user-select: none;

    &:focus {
      outline: 2px solid rgba($primary, 0.35);
      outline-offset: -2px;
    }
  }

  .settlement-sort-indicator {
    display: inline-block;
    min-width: 12px;
    color: #b8aeb3;
  }

  .settlement-group-header {
    text-align: center;
    font-weight: 700;
    vertical-align: middle;
    border-bottom-width: 2px;
  }
}

.settlement-hidden-field-header {
  display: none;
}

// โหมดสว่างได้โทนแดงจาง ๆ มาจาก .table-active ของธีมเอง (#f8f1f3 = primary ผสมบนขาว)
// แต่ธีมทำคู่โหมดมืดไว้แค่สีอักษรของ tbody แถวรวมที่อยู่ใน tfoot จึงค้างเป็นแผ่นขาวกลางตารางเข้ม
// ซ้อน primary แบบโปร่งด้วย gradient แทนการผสมสีสำเร็จ เพื่อคงพื้นของธีมไว้ข้างใต้และปรับความอิ่มที่จุดเดียว
.dark-layout {
  .settlement-group-row th {
    background-image: linear-gradient(rgba($primary, 0.07), rgba($primary, 0.07));
  }

  .table tfoot tr.table-active > td {
    background-color: $theme-dark-card-bg;
    background-image: linear-gradient(rgba($primary, 0.14), rgba($primary, 0.14));
    color: $theme-dark-body-color;
  }
}

.settlement-table-wrap {
  position: relative;
}

.settlement-pagination-row {
  align-items: center;
  row-gap: 0.75rem; // จอเล็กสามบล็อกซ้อนกัน ต้องมีระยะหายใจเท่า ๆ กัน
}

// ตัวเลขในแถวหลอกถูกกลบด้วยแถบ shimmer ที่วางในกรอบเดียวกับ padding ของเซลล์
// ความสูงทุกส่วนจึงมาจากตารางจริง ไม่ใช่ตัวเลขที่ตั้งไว้ตายตัวแล้วค่อยเพี้ยนตามหลัง
.settlement-table--loading {
  .table {
    // ตัวอักษรยังอยู่ครบเพื่อค้ำความสูงและความกว้างคอลัมน์ แค่มองไม่เห็น
    tbody td,
    tfoot td {
      position: relative;

      &,
      * {
        color: transparent !important;
      }

      &::after {
        content: '';
        position: absolute;
        top: 0.72rem;
        bottom: 0.72rem;
        left: 1rem;
        right: 1rem;
        border-radius: 3px;
        background: linear-gradient(90deg, #f0f0f0 25%, #e4e4e4 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
      }
    }
  }

  .skeleton-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.35);
  }
}

// โหมดมืดใช้โทนการ์ดชุดเดียวกับตารางจริง ไม่งั้นเป็นแผ่นขาวแสบตากลางจอ
.dark-layout .settlement-table--loading {
  .table {
    tbody td::after,
    tfoot td::after {
      background: linear-gradient(90deg, #313a52 25%, #3b4253 50%, #313a52 75%);
      background-size: 200% 100%;
    }
  }

  .skeleton-overlay {
    background: rgba(40, 48, 70, 0.35);
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
